import { useEffect, useState } from "react";
import { api, apiError, eur } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Paperclip, Send } from "lucide-react";

// Anteprima dell'email di proposta: il Partner controlla destinatario, testo e allegati prima dell'invio.
export const InviaClienteDialog = ({ qid, open, onOpenChange, onSent }) => {
  const [d, setD] = useState(null);
  const [oggetto, setOggetto] = useState("");
  const [messaggio, setMessaggio] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setD(null);
    api.get(`/quotes/${qid}/invia-cliente/anteprima`).then(({ data }) => { setD(data); setOggetto(data.oggetto); setMessaggio(data.messaggio); })
      .catch((e) => { toast.error(apiError(e)); onOpenChange(false); });
  }, [open, qid, onOpenChange]);
  const invia = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${qid}/invia-cliente`, { oggetto, messaggio });
      toast.success(`Proposta inviata a ${data.destinatario} con ${(data.allegati || []).length} allegati — copia al Partner`);
      onOpenChange(false); onSent?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="invia-cliente-dialog">
        <DialogHeader><DialogTitle className="font-heading text-forest">Controlla l'email prima di inviarla</DialogTitle></DialogHeader>
        {!d ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-forest" /> : (
          <div className="space-y-3 text-sm">
            {!["preventivo_emesso", "proposta", "in_attesa_bonifico", "accettato_cliente"].includes(d.stato) && (
              <p className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800" data-testid="invia-cliente-avviso-stato">
                Il preventivo non è ancora emesso: il cliente riceverà i documenti, ma potrà accettare online solo dopo che avrai cliccato «Emetti preventivo».
              </p>
            )}
            <div className="grid gap-1 rounded-lg bg-sage/40 p-3 text-forest">
              <div><span className="text-muted-foreground">A:</span> <b data-testid="invia-cliente-dest">{d.destinatario}</b></div>
              {d.cc?.length > 0 && <div><span className="text-muted-foreground">Copia a:</span> {d.cc.join(", ")}</div>}
            </div>
            <label className="block space-y-1"><span className="text-xs font-medium text-forest">Oggetto</span>
              <Input value={oggetto} onChange={(e) => setOggetto(e.target.value)} data-testid="invia-cliente-oggetto" /></label>
            <label className="block space-y-1"><span className="text-xs font-medium text-forest">Messaggio (modificabile)</span>
              <Textarea rows={6} value={messaggio} onChange={(e) => setMessaggio(e.target.value)} data-testid="invia-cliente-messaggio" /></label>
            <div className="rounded-lg border p-3" data-testid="invia-cliente-fisso">
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-forest/70">Aggiunto in automatico</div>
              <div className="mb-1 font-medium text-forest">Documenti allegati ({d.allegati.length})</div>
              <ul className="mb-2 space-y-0.5" data-testid="invia-cliente-allegati">{d.allegati.map((a) => <li key={a} className="flex items-center gap-1.5 text-muted-foreground"><Paperclip className="h-3.5 w-3.5" />{a}</li>)}</ul>
              <div className="text-forest">Premio annuo lordo: <b>{eur(d.premio_finale)}</b></div>
              <div className="mt-1 text-forest">Pulsante <b>"Accetta la proposta"</b> → <a href={d.accept_url} target="_blank" rel="noreferrer" className="break-all text-terracotta underline" data-testid="invia-cliente-link">{d.accept_url}</a></div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={invia} disabled={!d || busy || !messaggio.trim()} className="gap-1.5 bg-terracotta hover:bg-terracotta-hover" data-testid="invia-cliente-conferma">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Invia al cliente</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
